import { Link } from "react-router-dom";
import { seedAccommodations, typeLabels, typeColors, typeImages, Accommodation } from "@/mocks/accommodations";

interface SimilarAccommodationsProps {
  currentId: string;
  city: string;
  businessType: string;
}

const SimilarAccommodations = ({ currentId, city, businessType }: SimilarAccommodationsProps) => {
  const allAccommodations: Accommodation[] = (() => {
    try {
      const approved = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]")
        .filter((a: Accommodation) => a.status === "approved");
      return [...seedAccommodations, ...approved];
    } catch {
      return seedAccommodations;
    }
  })();

  // Same city first, then same type, exclude current
  const sameCity = allAccommodations.filter(
    (a) => a.id !== currentId && a.city === city
  );
  const sameType = allAccommodations.filter(
    (a) => a.id !== currentId && a.businessType === businessType && a.city !== city
  );

  const combined = [...sameCity, ...sameType];

  // Gold işletmeleri önce sırala
  const similar = combined
    .sort((a, b) => {
      if (a.membershipTier === "gold" && b.membershipTier !== "gold") return -1;
      if (a.membershipTier !== "gold" && b.membershipTier === "gold") return 1;
      return 0;
    })
    .slice(0, 4);

  if (similar.length === 0) return null;

  const goldCount = similar.filter((a) => a.membershipTier === "gold").length;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
          Benzer İşletmeler
        </h2>
        {goldCount > 0 && (
          <span className="flex items-center gap-1.5 text-xs text-amber-600 font-semibold bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
            <i className="ri-vip-crown-fill text-xs"></i>
            {goldCount} Gold işletme öne çıkarıldı
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {similar.map((acc) => {
          const imgs = typeImages[acc.businessType] || typeImages.hotel;
          const isSameCity = acc.city === city;
          const isGold = acc.membershipTier === "gold";

          return (
            <Link
              key={acc.id}
              to={`/accommodation/${acc.id}`}
              className={`group block rounded-2xl overflow-hidden transition-all duration-200 ${
                isGold
                  ? "border border-amber-300 bg-white"
                  : "border border-stone-100 bg-white hover:border-amber-200"
              }`}
              style={isGold ? { boxShadow: "0 0 0 1px rgba(245,158,11,0.12), 0 2px 12px rgba(245,158,11,0.08)" } : {}}
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={imgs[0]}
                  alt={acc.businessName}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${isGold ? "from-black/65 via-black/10 to-transparent" : "from-black/60 to-transparent"}`}></div>

                {/* Top badges */}
                <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[acc.businessType] || "bg-stone-100 text-stone-600"}`}>
                    {typeLabels[acc.businessType] || acc.businessType}
                  </span>
                  {isSameCity && !isGold && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-500/90 text-white">
                      Aynı Şehir
                    </span>
                  )}
                </div>

                {/* Gold badge top-right */}
                {isGold && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full px-2 py-0.5">
                    <i className="ri-vip-crown-fill text-white text-xs"></i>
                    <span className="text-white text-xs font-bold">GOLD</span>
                  </div>
                )}

                {/* Bottom name */}
                <div className="absolute bottom-2 left-3 right-3">
                  <p className="text-white font-semibold text-sm leading-tight line-clamp-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {acc.businessName}
                  </p>
                  {isSameCity && isGold && (
                    <span className="text-amber-300 text-xs">Aynı Şehir</span>
                  )}
                </div>
              </div>

              {/* Gold "Öne Çıkan" banner */}
              {isGold && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-100">
                  <i className="ri-shield-check-fill text-amber-500 text-xs"></i>
                  <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Öne Çıkan İşletme</span>
                  <div className="flex-1"></div>
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map((s) => (
                      <i key={s} className="ri-star-fill text-amber-400 text-xs"></i>
                    ))}
                  </div>
                </div>
              )}

              <div className="p-3">
                <div className="flex items-center gap-1 mb-1.5">
                  <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-amber-500 text-xs"></i>
                  </div>
                  <span className="text-xs text-stone-500">{acc.city} · {acc.district}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-bold ${isGold ? "text-amber-600" : "text-amber-600"}`}>
                    {acc.priceRange.split(" ")[0]}
                  </span>
                  {acc.starRating && (
                    <div className="flex items-center gap-0.5">
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <span className="text-xs text-stone-600 font-medium">{acc.starRating} Yıldız</span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default SimilarAccommodations;
